import { ThemeProvider, createTheme } from '@mui/material/styles';
import React, {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';
import { red } from '@mui/material/colors';
import { PaletteMode } from '@mui/material';
import neue from '../assets/fonts/NeueHaasUnicaW1G.ttf';

const ThemeContext = createContext({ toggleColorMode: () => {} });

export function Theme({ children }: { children: ReactNode }): JSX.Element {
  const [mode, setMode] = useState<PaletteMode>('light');
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [],
  );

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: mode === 'light' ? '#45009C' : '#FFFFFF',
          },
          secondary: {
            main: '#19857b',
          },
          error: {
            main: red.A400,
          },
          background: {
            default: mode === 'light' ? '#F9F8FB' : '#000000',
          },
          text: {
            primary: mode === 'light' ? '#17052d' : '#FFFFFF',
          },
          mode: mode,
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: `
              @font-face {
                font-family: 'neue-haas-unica';
                font-style: normal;
                font-display: swap;
                font-weight: 400;
                src: local('neue-haas-unica'), url(${neue}) format('truetype');
              }
              
              /* Disable unwanted entrance animations */
              .MuiPaper-root,
              .MuiCard-root,
              .MuiBox-root,
              .MuiContainer-root {
                animation: none !important;
                transform: none !important;
              }
              
              /* Disable transitions for data loading states */
              .MuiSkeleton-root,
              .MuiCircularProgress-root {
                transition: none !important;
              }
            `,
          },
          MuiButton: {
            styleOverrides: {
              outlined: {
                color: mode === 'light' ? '#45009C' : '#FFFFFF',
                borderColor: mode === 'light' ? '#45009C' : '#FFFFFF',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                transition: 'box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
              },
            },
          },
        },
        typography: {
          fontFamily: `neue-haas-unica`,
          fontSize: 14,
          fontWeightLight: 300,
          fontWeightRegular: 400,
          fontWeightMedium: 500,
        },
      }),
    [mode],
  );

  return (
    <ThemeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
}

export default function ToggleTheme() {
  return useContext(ThemeContext);
}
